import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PlusGate } from "@/components/Common";
import { Logo } from "@/components/AppShell";
import { Switch } from "@/components/ui/switch";

const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
const short = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
const v = (x, u = "") => (x == null ? "—" : `${x}${u}`);

const Section = ({ title, children, id }) => (
  <section className="report-section mt-8" data-testid={`report-section-${id}`}>
    <h2 className="font-display text-xl text-stone-900 border-b border-stone-200 pb-2 mb-3">{title}</h2>
    {children}
  </section>
);

const Table = ({ head, rows, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm report-table" data-testid={testid}>
      <thead><tr>{head.map((h) => <th key={h} className="text-left font-semibold text-stone-500 text-xs uppercase tracking-wide py-2 pr-3">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r) => <tr key={r[0]} className="border-t border-stone-100">{r.map((c, j) => <td key={head[j] ?? "note"} className="py-2 pr-3 align-top">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

function Controls({ months, setMonths, notes, setNotes }) {
  return (
    <div className="no-print card p-4 mb-6 flex flex-wrap items-center gap-4 justify-between">
      <div className="inline-flex p-1 rounded-full bg-[#EFECE6]">
        {[1, 2, 3].map((m) => (
          <button key={m} onClick={() => setMonths(m)} data-testid={`report-range-${m}`}
            className={`chip px-4 py-2 rounded-full text-sm font-medium ${months === m ? "bg-white shadow-sm" : "text-stone-500"}`}>{m} month{m > 1 ? "s" : ""}</button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-stone-600">
        <Switch data-testid="report-notes-switch" checked={notes} onCheckedChange={setNotes} /> Include my private notes
      </label>
      <button onClick={() => window.print()} data-testid="report-print-btn" className="btn-ink px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2">
        <Printer className="h-4 w-4" /> Print or save PDF
      </button>
    </div>
  );
}

function CycleSection({ c }) {
  return (
    <Section title="Cycle" id="cycle">
      <p className="text-sm text-stone-700 mb-3">
        Period starts logged: {c.period_starts.length ? c.period_starts.map(short).join(", ") : "none in this range"}.
        {c.cycle_lengths.length > 0 && <> Cycle lengths: {c.cycle_lengths.join(", ")} days (average {c.avg_length}).</>}
        {" "}Typical length she has set: {v(c.setting_length, " days")}.
      </p>
      <Table testid="report-phase-table" head={["Estimated phase", "Days", "Avg mood", "Avg energy", "Most common symptoms"]}
        rows={c.phases.map((p) => [p.phase[0].toUpperCase() + p.phase.slice(1), p.days, v(p.mood), v(p.energy), p.top_symptoms.join(", ") || "—"])} />
      <p className="text-xs text-stone-500 mt-2">Phases are estimated from the last period start and typical cycle length, not measured.</p>
    </Section>
  );
}

function ReportBody({ r, notes }) {
  return (
    <article className="card p-6 sm:p-10 report-sheet" data-testid="report-sheet">
      <header className="flex items-start justify-between gap-4 border-b border-stone-200 pb-5">
        <div>
          <div className="eyebrow mb-1">Self-tracked wellness summary</div>
          <h1 className="font-display text-3xl tracking-tight" data-testid="report-name">{r.name}</h1>
          <p className="text-sm text-stone-600 mt-1" data-testid="report-range">{fmt(r.start)} to {fmt(r.end)} · {r.days_logged} of {r.days_in_range} days logged</p>
          {r.goals.length > 0 && <p className="text-sm text-stone-600">Her focus: {r.goals.join(", ")}</p>}
        </div>
        <Logo />
      </header>

      <Section title="Averages" id="averages">
        <Table testid="report-metrics-table" head={["Measure", "Average", "Range", "Days recorded"]}
          rows={r.metrics.map((m) => [m.label, v(m.avg, m.unit), m.n ? `${m.min}–${m.max}` : "—", m.n])} />
        <p className="text-xs text-stone-500 mt-2">Scales are 1 (low) to 5 (high). For stress, higher means more stressed.</p>
      </Section>

      <Section title="Week by week" id="weeks">
        <Table testid="report-weeks-table" head={["Week", "Days logged", "Mood", "Energy", "Sleep (h)", "Stress"]}
          rows={r.weeks.map((w) => [`${short(w.start)}–${short(w.end)}`, w.logged, v(w.mood), v(w.energy), v(w.sleep_hours), v(w.stress)])} />
      </Section>

      <Section title="Symptoms" id="symptoms">
        {r.symptoms.length ? <Table testid="report-symptoms-table" head={["Symptom", "Days", "% of logged days"]} rows={r.symptoms.map((s) => [s.name, s.days, `${s.pct}%`])} />
          : <p className="text-sm text-stone-600">No symptoms logged in this range.</p>}
      </Section>

      {r.cycle && <CycleSection c={r.cycle} />}

      {r.patterns.length > 0 && (
        <Section title="Patterns in her own data" id="patterns">
          <ul className="text-sm space-y-1.5 list-disc pl-5">{r.patterns.map((p) => <li key={p.id}>{p.text} <span className="text-stone-500">(based on {p.based_on} days)</span></li>)}</ul>
        </Section>
      )}

      <Section title="Daily log" id="daily">
        <Table testid="report-daily-table" head={["Date", "Mood", "Energy", "Sleep", "Stress", "Symptoms", "Flow", ...(notes ? ["Note"] : [])]}
          rows={r.daily.map((d) => [short(d.date), v(d.mood), v(d.energy), v(d.sleep_hours), v(d.stress), (d.symptoms || []).join(", ") || "—", d.cycle_flow || "—", ...(notes ? [d.note || ""] : [])])} />
      </Section>

      <footer className="mt-10 pt-4 border-t border-stone-200 text-xs text-stone-500 leading-relaxed">
        Recorded by the patient in CAMRYN, a wellness app. These are self-reported observations, not clinical measurements or a diagnosis. Generated {fmt(new Date().toISOString().slice(0, 10))}.
      </footer>
    </article>
  );
}

export default function Report() {
  const { user } = useAuth();
  const [months, setMonths] = useState(1);
  const [notes, setNotes] = useState(false);
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const plus = user.plan === "plus";

  useEffect(() => {
    if (!plus) return;
    setR(null);
    api.get(`/report?months=${months}`).then(({ data }) => setR(data)).catch((e) => setErr(errMsg(e)));
  }, [months, plus]);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/you" data-testid="report-back-link" className="no-print inline-flex items-center gap-1.5 text-sm text-stone-600 mb-5"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <div className="no-print mb-6">
          <div className="eyebrow mb-2">For your appointment</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Clinician report</h1>
          <p className="text-stone-600 mt-2 text-sm sm:text-base">A clear, factual summary of what you've logged, to print or save as a PDF and bring to your doctor.</p>
        </div>
        {!plus ? <PlusGate testid="report-plus-gate" title="Bring your story to your doctor" body="Plus turns 1 to 3 months of check-ins into a printable summary: averages, weekly trends, symptoms and cycle." />
          : <>
            <Controls months={months} setMonths={setMonths} notes={notes} setNotes={setNotes} />
            {err ? <p className="text-sm text-red-700" data-testid="report-error">{err}</p>
              : !r ? <div className="mx-auto mt-16 h-8 w-8 rounded-full border-2 border-[#C86D51] border-t-transparent animate-spin" data-testid="report-loading" />
              : <ReportBody r={r} notes={notes} />}
          </>}
      </div>
    </div>
  );
}
